import React, { useContext, useEffect, useReducer } from 'react';
import {
	RestaurantFullInfoContext,
	restaurantFullInfoInitialState,
	TRestaurantFullInfoVisibleForm,
	TSelectedLocalizationItem,
	ISelectedStyleGroup,
} from './restaurantFullInfoContext';
import { restaurantFullInfoReducer } from './restaurantFullInfoReducer';
import { AppContext, IAppContext } from 'appState/appContext';
import { RestaurantFullInfoActionTypes } from './restaurantFullInfoActions';
import {
	TLanguageIDs,
	IDataGetRestaurantInfo,
	IGetLatestSoftwareUpdateInfo,
	IGetRestaurantInfo,
	IGetRestaurantLocalizationInfo,
	IGetRestaurantStylesInfo,
	ILatestSoftwareUpdateInfo,
	IRestaurantLocalizationInfo,
	IRestaurantStylesInfo,
	IRestaurantReportsInfo,
	IGetRestaurantReportsInfo,
	IGetRestaurantLocalizationInfoArgs,
	IGetRestaurantAndroidWatchesMessages,
	IRestaurantAndroidWatchesMessagesData,
} from 'types';
import { NON_EXISTENT_KEY } from 'appConstants';
import { IRestaurantsContext, RestaurantsContext } from '../../Restaurants/restaurantsState/restaurantsContext';
import {
	getRestaurantInfo,
	getRestaurantLocalizationInfo,
	getRestaurantStylesInfo,
	getLatestRestaurantSoftwareUpdatesData,
	getRestaurantReportsInfo,
} from 'backend/restaurants';
import { getRestaurantAndroidWatchesMessages } from 'backend/watches';
import existingMediaSettingsKeys from 'public/mediaSettings.json';
import { useFetchData } from 'hooks/useFetchData';

const RestaurantFullInfoState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(restaurantFullInfoReducer, restaurantFullInfoInitialState);
	const { showLoader, hideLoader, featureFlags } = useContext<IAppContext>(AppContext);
	const { fullInfoData } = useContext<IRestaurantsContext>(RestaurantsContext);

	const needLocalizationDataRequest =
		state.restaurantForFullInfo &&
		!state.localizationData &&
		state.restaurantFullInfoVisibleForm === 'localizationForm';

	const needStyleDataRequest =
		state.restaurantForFullInfo && !state.styleData && state.restaurantFullInfoVisibleForm === 'styleForm';

	const needSoftwareUpdatesRequest =
		state.restaurantForFullInfo &&
		!state.latestSoftwareUpdateData &&
		state.restaurantFullInfoVisibleForm === 'softwareForm';

	const needReportRequest =
		state.restaurantForFullInfo && !state.report && state.restaurantFullInfoVisibleForm === 'reportsForm';

	const needAndroidWatchesRequest =
		state.restaurantForFullInfo &&
		!state.androidWatches &&
		state.restaurantFullInfoVisibleForm === 'androidWatchesForm';

	const {
		response: restaurantDataResponse,
		refetch: restaurantDataRefetch,
		loading: restaurantDataLoading,
	} = useFetchData<IGetRestaurantInfo, string>(fullInfoData.id ? getRestaurantInfo : null, fullInfoData.id);

	const {
		response: localizationDataResponse,
		refetch: localizationDataRefetch,
		loading: localizationDataLoading,
	} = useFetchData<IGetRestaurantLocalizationInfo, IGetRestaurantLocalizationInfoArgs>(
		needLocalizationDataRequest ? getRestaurantLocalizationInfo : null,
		{
			customerID: state.restaurantForFullInfo?.customerId || '',
			restaurantID: state.restaurantForFullInfo?.id || '',
		},
	);

	const {
		response: styleDataResponse,
		refetch: styleDataRefetch,
		loading: styleDataLoading,
	} = useFetchData<IGetRestaurantStylesInfo, string>(
		needStyleDataRequest ? getRestaurantStylesInfo : null,
		state.restaurantForFullInfo?.customer.id || '',
		state.restaurantForFullInfo?.id || '',
	);

	const { response: latestSoftwareUpdatesDataResponse, refetch: latestSoftwareUpdatesDataRefetch } = useFetchData<
		IGetLatestSoftwareUpdateInfo,
		string
	>(
		needSoftwareUpdatesRequest ? getLatestRestaurantSoftwareUpdatesData : null,
		state.restaurantForFullInfo?.id || '',
	);

	const {
		response: reportDataResponse,
		refetch: reportDataRefetch,
		loading: reportDataLoading,
	} = useFetchData<IGetRestaurantReportsInfo, string>(
		needReportRequest ? getRestaurantReportsInfo : null,
		state.restaurantForFullInfo?.id || '',
	);

	const {
		response: androidWatchesDataResponse,
		refetch: androidWatchesDataRefetch,
		loading: androidWatchesDataLoading,
	} = useFetchData<IGetRestaurantAndroidWatchesMessages, string>(
		needAndroidWatchesRequest ? getRestaurantAndroidWatchesMessages : null,
		state.restaurantForFullInfo?.id || '',
	);

	useEffect(() => {
		restaurantDataLoading ||
		localizationDataLoading ||
		styleDataLoading ||
		reportDataLoading ||
		androidWatchesDataLoading
			? showLoader()
			: hideLoader();
	}, [
		restaurantDataLoading,
		localizationDataLoading,
		styleDataLoading,
		reportDataLoading,
		androidWatchesDataLoading,
	]);

	const changeRestaurantFullInfoVisibleForm = (value: TRestaurantFullInfoVisibleForm) => {
		dispatch({
			type: RestaurantFullInfoActionTypes.CHANGE_RESTAURANT_FULL_INFO_VISIBLE_FORM,
			payload: {
				restaurantFullInfoVisibleForm: value,
			},
		});
	};

	const setRestaurantForFullInfo = (value: Nullable<IDataGetRestaurantInfo>) => {
		dispatch({
			type: RestaurantFullInfoActionTypes.SET_RESTAURANT_FOR_FULL_INFO,
			payload: {
				restaurantForFullInfo: value,
			},
		});
	};

	const setLocalizationGroup = (value: Nullable<TSelectedLocalizationItem>) => {
		dispatch({
			type: RestaurantFullInfoActionTypes.SET_LOCALIZATION_GROUP,
			payload: {
				selectedLocalizationGroup: value,
			},
		});
	};

	const setLocalizationLanguage = (value: Nullable<TSelectedLocalizationItem>) => {
		dispatch({
			type: RestaurantFullInfoActionTypes.SET_LOCALIZATION_LANGUAGE,
			payload: {
				selectedLocalizationLanguage: value,
			},
		});
	};

	const setLocalizationData = (value: Nullable<IRestaurantLocalizationInfo>) => {
		dispatch({
			type: RestaurantFullInfoActionTypes.SET_LOCALIZATION_DATA,
			payload: {
				localizationData: value,
			},
		});
	};

	const setStyleData = (value: Nullable<IRestaurantStylesInfo>) => {
		if (value) {
			const filteredDocumentSettings = value.documentSettings.filter((el) =>
				Object.keys(existingMediaSettingsKeys).includes(el.key || NON_EXISTENT_KEY),
			);
			dispatch({
				type: RestaurantFullInfoActionTypes.SET_STYLE_DATA,
				payload: {
					styleData: { ...value, documentSettings: filteredDocumentSettings },
				},
			});
		} else {
			dispatch({
				type: RestaurantFullInfoActionTypes.SET_STYLE_DATA,
				payload: {
					styleData: value,
				},
			});
		}
	};

	const setStyleGroup = (value: Nullable<ISelectedStyleGroup>) => {
		dispatch({
			type: RestaurantFullInfoActionTypes.SET_STYLE_GROUP,
			payload: {
				selectedStyleGroup: value,
			},
		});
	};

	const setStyleLanguage = (value: TLanguageIDs) => {
		dispatch({
			type: RestaurantFullInfoActionTypes.SET_STYLE_LANGUAGE,
			payload: {
				selectedStyleLanguage: value,
			},
		});
	};

	const setLatestSoftwareUpdateData = (value: Nullable<ILatestSoftwareUpdateInfo>) => {
		dispatch({
			type: RestaurantFullInfoActionTypes.SET_LATEST_SOFTWARE_UPDATE_DATA,
			payload: {
				latestSoftwareUpdateData: value,
			},
		});
	};

	const setReport = (value: Nullable<IRestaurantReportsInfo>) => {
		dispatch({
			type: RestaurantFullInfoActionTypes.SET_REPORT,
			payload: {
				report: value,
			},
		});
	};

	const setAndroidWatches = (value: Nullable<IRestaurantAndroidWatchesMessagesData>) => {
		dispatch({
			type: RestaurantFullInfoActionTypes.SET_ANDROID_WATCHES,
			payload: {
				androidWatches: value,
			},
		});
	};

	useEffect(() => {
		restaurantDataRefetch();

		return () => {
			setRestaurantForFullInfo(null);
			changeRestaurantFullInfoVisibleForm('mainForm');
			setLocalizationData(null);
			setStyleData(null);
			setStyleGroup(null);
			setLatestSoftwareUpdateData(null);
			setReport(null);
			setAndroidWatches(null);
		};
	}, [fullInfoData.id]);

	useEffect(() => {
		restaurantDataResponse && setRestaurantForFullInfo(restaurantDataResponse.data.restaurant);
	}, [restaurantDataResponse]);

	useEffect(() => {
		localizationDataRefetch();
	}, [state.restaurantForFullInfo, state.restaurantFullInfoVisibleForm, state.localizationData]);

	useEffect(() => {
		localizationDataResponse && setLocalizationData(localizationDataResponse.data);
	}, [localizationDataResponse]);

	useEffect(() => {
		styleDataRefetch();
	}, [state.restaurantForFullInfo, state.restaurantFullInfoVisibleForm, state.styleData]);

	useEffect(() => {
		if (styleDataResponse) {
			const updatedMediaSettings = styleDataResponse.data.mediaSettings.map((mediaSetting) => {
				const filteredMediaSettingGroups = mediaSetting.groups.filter((group) => {
					if (group.key === 'fiscal_receipt_dialog_background') {
						return featureFlags.HAS_FISCAL_RECEIPT_DIALOG_BACKGROUND;
					}
					return true;
				});

				return { ...mediaSetting, groups: filteredMediaSettingGroups };
			});

			setStyleData({ ...styleDataResponse.data, mediaSettings: updatedMediaSettings });
		}
	}, [styleDataResponse]);

	useEffect(() => {
		latestSoftwareUpdatesDataResponse && setLatestSoftwareUpdateData(latestSoftwareUpdatesDataResponse.data);
	}, [latestSoftwareUpdatesDataResponse]);

	useEffect(() => {
		latestSoftwareUpdatesDataRefetch();
	}, [state.restaurantForFullInfo, state.restaurantFullInfoVisibleForm, state.latestSoftwareUpdateData]);

	useEffect(() => {
		// reportResponse && setReport(reportResponse.data.report);
		reportDataResponse && setReport(null);
	}, [reportDataResponse]);

	useEffect(() => {
		reportDataRefetch();
	}, [state.restaurantForFullInfo, state.restaurantFullInfoVisibleForm, state.report]);

	useEffect(() => {
		androidWatchesDataResponse && setAndroidWatches(androidWatchesDataResponse.data);
	}, [androidWatchesDataResponse]);

	useEffect(() => {
		androidWatchesDataRefetch();
	}, [state.restaurantForFullInfo, state.restaurantFullInfoVisibleForm, state.androidWatches]);

	return (
		<RestaurantFullInfoContext.Provider
			value={{
				changeRestaurantFullInfoVisibleForm,
				setLocalizationGroup,
				setLocalizationLanguage,
				setStyleGroup,
				setStyleLanguage,
				restaurantFullInfoVisibleForm: state.restaurantFullInfoVisibleForm,
				restaurantForFullInfo: state.restaurantForFullInfo,
				selectedLocalizationGroup: state.selectedLocalizationGroup,
				selectedLocalizationLanguage: state.selectedLocalizationLanguage,
				localizationData: state.localizationData,
				styleData: state.styleData,
				selectedStyleGroup: state.selectedStyleGroup,
				selectedStyleLanguage: state.selectedStyleLanguage,
				latestSoftwareUpdateData: state.latestSoftwareUpdateData,
				report: state.report,
				androidWatches: state.androidWatches,
			}}>
			{children}
		</RestaurantFullInfoContext.Provider>
	);
};

export default RestaurantFullInfoState;
